import { Link } from "react-router-dom";
import { Eye, Settings2, Inbox, Pencil, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { assetUrl } from "@/lib/api";

const Icon = ({ as: C, ...p }) => <C className="w-4 h-4" {...p} />;
const act = "p-2 rounded-full text-stone-500 hover:bg-stone-100 hover:text-stone-900 transition-colors";

const DeleteButton = ({ school, onDelete }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <button className={`${act} hover:text-red-600`} title="Delete" data-testid={`school-delete-${school.slug}`}><Icon as={Trash2} /></button>
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Delete {school.config.institute_name}?</AlertDialogTitle>
        <AlertDialogDescription>This permanently removes the website and all {school.lead_count} of its enquiries. This can't be undone.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="school-delete-cancel">Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={() => onDelete(school)} className="bg-red-600 hover:bg-red-700" data-testid="school-delete-confirm">Delete school</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export default function SchoolRow({ s, rootDomain, onToggle, onEdit, onDelete }) {
  const c = s.config;
  return (
    <tr className="border-t" data-testid={`school-row-${s.slug}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-[200px]">
          {c.logo_url
            ? <img src={assetUrl(c.logo_url)} alt="" className="w-9 h-9 rounded-lg object-contain border bg-white" />
            : <div className="w-9 h-9 rounded-lg text-white flex items-center justify-center font-display font-semibold" style={{ background: c.primary_color }}>{c.institute_name[0]}</div>}
          <div>
            <p className="font-medium" data-testid="school-row-name">{c.institute_name}</p>
            <p className="text-xs text-stone-500">{s.admin_emails.length ? s.admin_emails.join(", ") : "No school admin yet"}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <p className="font-mono text-xs" data-testid="school-row-subdomain">{s.slug}.{rootDomain}</p>
        <Link to={`/s/${s.slug}`} target="_blank" className="text-xs text-brand hover:underline" data-testid="school-row-path-link">/s/{s.slug}</Link>
      </td>
      <td className="px-4 py-3 text-center font-semibold" data-testid="school-row-lead-count">{s.lead_count}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Switch checked={s.active} onCheckedChange={(v) => onToggle(s, v)} data-testid={`school-active-switch-${s.slug}`} />
          <span className={`text-xs font-medium ${s.active ? "text-green-700" : "text-stone-400"}`}>{s.active ? "Live" : "Paused"}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end">
          <a href={`/s/${s.slug}`} target="_blank" rel="noreferrer" className={act} title="Preview website" data-testid={`school-preview-${s.slug}`}><Icon as={Eye} /></a>
          <Link to={`/admin/schools/${s.id}/settings`} className={act} title="Edit website content" data-testid={`school-manage-${s.slug}`}><Icon as={Settings2} /></Link>
          <Link to={`/admin/schools/${s.id}`} className={act} title="School's enquiries" data-testid={`school-leads-${s.slug}`}><Icon as={Inbox} /></Link>
          <button onClick={() => onEdit(s)} className={act} title="Edit subdomain & admins" data-testid={`school-edit-${s.slug}`}><Icon as={Pencil} /></button>
          <DeleteButton school={s} onDelete={onDelete} />
        </div>
      </td>
    </tr>
  );
}
